import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { BackendTypes } from '@tf/api';
import { Input, Select } from '@tf/ui';
import { C } from '@tf/utils';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useConfigContext, useDisabledAutocompleteAttributes } from '../../../hooks';
import { TooltipIcon } from '../TooltipIcon';
import type { DefaultInputProps } from '../types';

interface Props extends DefaultInputProps {
	enumName?: string;
	isSearchable?: boolean;
	options?: BackendTypes.SelectOption[];
	currency?: string;
}

export default function SelectInput({
	name,
	label,
	description,
	enumName,
	placeholder,
	isReadOnly,
	isSearchable,
	isRequired,
	className,
	tooltip,
	currency,
	...props
}: Props) {
	const { _ } = useLingui();
	const form = useFormContext();
	const getEnumOptions = useConfigContext((s) => s.getEnumOptions);
	const options = enumName ? getEnumOptions(enumName) : props.options || [];

	useEffect(() => {
		const value = form.getValues(name);
		form.setValue(name, value || undefined);
	}, [name, form.setValue, form.getValues]);

	const disabledAutocompleteAttributes = useDisabledAutocompleteAttributes();

	return (
		<Controller
			control={form.control}
			name={name}
			render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
				<Input.Wrapper
					id={name}
					className={className}
					label={`${label} ${currency ? `(${currency})` : ''}`}
					required={isRequired}
				>
					<Select
						id={name}
						ref={ref}
						value={value}
						onChange={onChange}
						onBlur={onBlur}
						error={Boolean(error)}
						placeholder={
							isReadOnly
								? _(
										msg({
											id: 'fb.selectInput.empty',
											message: 'Not specified',
										}),
									)
								: placeholder ||
									_(
										msg({
											id: 'fb.selectInput.placeholder',
											message: 'Select option...',
										}),
									)
						}
						description={description}
						data={C.groupCountries(options)}
						readOnly={isReadOnly}
						data-readonly={isReadOnly}
						rightSectionPointerEvents="initial"
						rightSection={
							tooltip || error ? <TooltipIcon tooltip={tooltip} error={error} /> : isReadOnly ? <div /> : undefined
						}
						searchable={isSearchable || options.length > 5}
						{...disabledAutocompleteAttributes}
					/>
				</Input.Wrapper>
			)}
		/>
	);
}
